define([
    "dojo/_base/declare",
    "dojo/_base/lang",
    "dojo/_base/window",
    "dojo/dom",
    "dojo/dom-class",
    "dojo/dom-construct",
    "dojo/dom-geometry",
    "dojo/dom-style",
    "dojo/mouse",
    "dojo/touch",
    "dojo/on",
    "dijit/popup",
    "./DocumentGroupProperties",
    "./Geometry",
    "./LayoutUtils",
    "./TabBar",
    "./TabGroupHeader"
], function (declare, lang, window, dom, domClass, domConstruct, domGeometry, domStyle, mouse, touch, on, popup,
             DocumentGroupProperties, Geometry, LayoutUtils, TabBar, TabGroupHeader) {

    var HEADER_THICKNESS = 20;  // TODO: Obtain from CSS
    var HEADER_DISPLAY_DELAY = 200;

    /**
     * Specializes the TabBar to group the tabs, display group headers and support group drag
     */
    return declare([TabBar], {

        // Private properties
        // _header
        // _connections

        addTab: function (widget, properties, index, immediate, dragInInfo) {
            var that = this;
            var tab = this.inherited(arguments);
            if (properties.groupId) {
                domClass.add(tab.domNode, "groupTab");
            }
            // Listen for mouse move over tab header and trigger display of group header
            if (properties.setId && properties.__showHeader) {
                tab._mouseEnterListener = on(tab.headerNode, mouse.enter, lang.hitch(this, "_initiateHeaderDisplay", tab, widget, properties));
            }
        },

        removeTab: function (properties, immediate) {
            var tab = this.inherited(arguments);
            if (tab && tab._mouseEnterListener) {
                // g2109649: Remove MouseEnter listener before removing the tab
                // to prevent GroupTabHeader from showing up.
                tab._mouseEnterListener.remove();
                tab._mouseEnterListener = null;
            }
        },

        _initiateHeaderDisplay : function (tab, widget, properties) {
            var that = this;
            if (!properties.groupId && this._getSetMemberCount(properties.setId) < 2)  {
                return;  // Don't show header for single tab
            }
            var headerBox = this._computeHeaderBounds(properties.setId, tab.headerNode);
            this._removeConnections();
            var timer = setTimeout(lang.hitch(this, "_showHeader", tab, widget, properties, headerBox), HEADER_DISPLAY_DELAY);
            this._connections = [
                on(this.ownerDocument, touch.move, function (e) {
                    if (that._header) {
                        // If moved off of header then hide it
                        if (e.target !== that._header.domNode &&
                            e.target !== tab.headerNode && !dom.isDescendant(e.target, that._header.domNode)) {
                           that._hideHeader();
                        }
                    } else {
                        // If moved outside prospective header display box then cancel the pending display
                        if (!Geometry.boxContains(headerBox, e.pageX, e.pageY)) {
                            clearTimeout(timer);
                            that._removeConnections();
                        }
                    }
                })
            ];
        },

        _showHeader: function (tab, widget, properties, box) {
            if (this._header || !this._connections) {
                return;
            }
            this._header = new TabGroupHeader({
                id: properties.setId + "Header",
                setId: properties.groupId || properties.setId,
                label: this.groupPropertiesFetcher(widget, properties).title,
                createMenu: lang.partial(this.actionsMenuCreator, properties.groupId ? tab.widget : properties.setId),
                menuCloseCallback: lang.hitch(this, "_hideHeader")
            });

            domClass.add(this._header.domNode, this.tabOpening + "Open");
            dom.setSelectable(this._header.domNode, false);
            LayoutUtils.preventFocus(this._header.domNode, true);
            this._header.placeAt(window.body());
            domGeometry.setMarginBox(this._header.domNode, box);
            // The following is needed to truncate the rotated label.
            // There does not appear to be an easy way to do this purely in CSS.
            if (this.orientation === "vertical") {
                domStyle.set(this._header.labelNode, {width: (box.h - 24) + "px"});
            }
            this._connections.push(on(this._header.domNode, mouse.leave, lang.hitch(this, "_hideHeader")));
            this._connections.push(on(this._header.domNode, touch.press, lang.hitch(this, "_beginSetDrag", properties.setId, box)));
        },

        _hideHeader: function () {
            // Don't hide the header if it is being dragged or if its menu is being shown
            if (this._header && !this._buttonDragInfo &&
                (popup._stack.length === 0 || (popup._stack[0].widget !== this._header.menu &&
                                               popup._stack[0].widget !== this._header.contextMenu))) {
                window.body().removeChild(this._header.domNode);
                this._header.destroy();
                this._removeConnections();
                delete this._header;
            }
        },

        _continueSetDrag: function () {
            this.inherited(arguments);
            this._updateHeaderPosition();
        },

        _updateButtonPositions: function () {
            this.inherited(arguments);
            this._updateHeaderPosition();
        },

        _updateHeaderPosition: function () {
            if (this._header) {
                // Get current box
                var headerBox = domGeometry.getMarginBox(this._header.domNode);

                // Get set bounds
                var setBounds = this._getVisibleButtonSetBounds(this._header.setId);
                // Convert to page bounds
                var barBox = domGeometry.position(this.domNode);
                setBounds.l += barBox.x;
                setBounds.t += barBox.y;
                // Update free dimension of header
                if (this.orientation === "horizontal") {
                    headerBox.l = setBounds.l;
                    headerBox.w = setBounds.w;
                } else {
                    headerBox.t = setBounds.t;
                    headerBox.h = setBounds.h;
                }
                domGeometry.setMarginBox(this._header.domNode, headerBox);
            }
        },

        _computeHeaderBounds: function (setId, tabHeader) {
            // Compute bounds relative to bar
            var bounds = this._getVisibleButtonSetBounds(setId);
            var tabHeaderBounds = domGeometry.getMarginBox(tabHeader);
            switch (this.tabOpening) {
                case "bottom":
                    bounds.t += tabHeaderBounds.t + tabHeaderBounds.h - HEADER_THICKNESS + 1;
                    bounds.h = HEADER_THICKNESS;
                    break;
                case "top":
                    bounds.t +=tabHeaderBounds.t;
                    bounds.h = HEADER_THICKNESS;
                    break;
                case "right":
                    bounds.l += tabHeaderBounds.l + tabHeaderBounds.w - HEADER_THICKNESS + 1;
                    bounds.w = HEADER_THICKNESS;
                    break;
                case "left":
                    bounds.l += tabHeaderBounds.l;
                    bounds.w = HEADER_THICKNESS;
            }
            // Convert to page bounds
            var barBox = domGeometry.position(this.domNode);
            bounds.l += barBox.x;
            bounds.t += barBox.y;

            // Restrict to document body bounds
            Geometry.constrainBox(bounds, {l: 0, t: 0, w: window.body().clientWidth, h: window.body().clientHeight});

            return bounds;
        },

        _removeConnections: function () {
            if (this._connections) {
                this._connections.forEach(function (connection) {
                    connection.remove();
                });
                delete this._connections;
            }
        }
    });
});
